import type {
  BorderTokens,
  ColorRamp,
  ElevationTokens,
  NeutralScale,
  StatusColors,
  SurfaceTokens,
  TextTokens,
  ThemeTokens,
} from './tokens';

/**
 * Turns a small preset seed into a complete token set, for either mode.
 *
 * A seed states only what is particular about a preset; every relationship
 * (hover tints, focus rings, table rows, sidebar chrome) is computed the same
 * way every time. Status colours are deliberately not derived from the brand,
 * so "error" looks like an error on every preset.
 */

// ── Colour maths ─────────────────────────────────────────────────────────────
// Hex colours only (#RGB or #RRGGBB).

const clamp = (n: number): number => Math.max(0, Math.min(255, Math.round(n)));

const hexToRgb = (hex: string): [number, number, number] => {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
};

const rgbToHex = (r: number, g: number, b: number): string =>
  `#${[r, g, b].map((v) => clamp(v).toString(16).padStart(2, '0')).join('')}`.toUpperCase();

/** Linear blend. `amount` 0 returns `from`, 1 returns `to`. */
export const mix = (from: string, to: string, amount: number): string => {
  const [r1, g1, b1] = hexToRgb(from);
  const [r2, g2, b2] = hexToRgb(to);
  return rgbToHex(r1 + (r2 - r1) * amount, g1 + (g2 - g1) * amount, b1 + (b2 - b1) * amount);
};

export const lighten = (hex: string, amount: number): string => mix(hex, '#FFFFFF', amount);
export const darken = (hex: string, amount: number): string => mix(hex, '#000000', amount);

/** rgba() string, for genuine alphas only (scrims, overlays on chrome). */
export const alphaOf = (hex: string, a: number): string => {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

/** WCAG relative luminance. */
const luminance = (hex: string): number => {
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const [r, g, b] = hexToRgb(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/** WCAG contrast ratio, 1–21. */
export const contrastRatio = (a: string, b: string): number => {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

/** Black or white, whichever is more legible on `background`. */
export const readableOn = (background: string, dark = '#1A1A1A', light = '#FFFFFF'): string =>
  contrastRatio(background, dark) >= contrastRatio(background, light) ? dark : light;

/** Builds a five-stop ramp from one colour. */
export const rampFrom = (main: string, contrastText?: string): ColorRamp => ({
  lighter: lighten(main, 0.85),
  light: lighten(main, 0.4),
  main,
  dark: darken(main, 0.2),
  darker: darken(main, 0.45),
  contrastText: contrastText ?? readableOn(main),
});

// ── Status ───────────────────────────────────────────────────────────────────

const STATUS_LIGHT: StatusColors = {
  info: rampFrom('#0F6CBD', '#FFFFFF'),
  success: rampFrom('#107C41', '#FFFFFF'),
  warning: rampFrom('#B26C00', '#FFFFFF'),
  error: rampFrom('#C4314B', '#FFFFFF'),
  neutral: rampFrom('#6B6B6B', '#FFFFFF'),
};

// Dark mode lifts each hue: the light values go muddy on a near-black canvas.
const STATUS_DARK: StatusColors = {
  info: rampFrom('#4DA3E8', '#0A0A0A'),
  success: rampFrom('#4CC38A', '#0A0A0A'),
  warning: rampFrom('#E2A03F', '#0A0A0A'),
  error: rampFrom('#F1707F', '#0A0A0A'),
  neutral: rampFrom('#9A9A9A', '#0A0A0A'),
};

// ── Seed ─────────────────────────────────────────────────────────────────────

/**
 * Per-mode pins for values a preset must hold exactly (e.g. an existing brand
 * palette). Applied as soon as each group is computed, so everything derived
 * from it — hover tints, focus rings, buttons — follows the pinned value.
 * Only hex colours may feed derivations; `border` may use rgba().
 */
export interface ModeOverrides {
  primary?: Partial<ColorRamp>;
  secondary?: Partial<ColorRamp>;
  status?: Partial<{ [K in keyof StatusColors]: Partial<ColorRamp> }>;
  surface?: Partial<SurfaceTokens>;
  text?: Partial<TextTokens>;
  border?: Partial<BorderTokens>;
}

/** What a preset actually has to state. Everything else is computed. */
export interface ThemeSeed {
  /** Brand colours. `secondary` and `accent` fall back to derivations of primary. */
  primary: string;
  primaryContrast?: string;
  secondary?: string;
  accent?: string;

  /** The hue the greys carry — this, more than the brand, makes a preset read warm or cool. */
  neutralHue?: string;
  /** 0 is pure grey. Above about 0.12 the greys start reading as a colour. */
  neutralSaturation?: number;

  /** Dark sidebar chrome on a light page. */
  darkSidebar?: boolean;
  /** Brand-coloured top bar rather than a surface-coloured one. */
  brandNav?: boolean;
  /** Filled brand drawer header. False gives a surface header. */
  brandDrawerHeader?: boolean;
  /** Zebra-striped tables. */
  stripedTables?: boolean;

  radius?: Partial<{ sm: number; md: number; lg: number; pill: number }>;
  fontFamily?: string;
  monoFamily?: string;
  baseSize?: number;
  headingLetterSpacing?: number;
  /** Flat presets pass 0 and get hairlines instead of shadows. */
  elevationStrength?: number;

  modes?: { light?: ModeOverrides; dark?: ModeOverrides };
}

export const DEFAULT_FONT = [
  '"Inter"',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  'Helvetica',
  'Arial',
  'sans-serif',
].join(',');

const DEFAULT_MONO = '"JetBrains Mono", "Cascadia Code", "Consolas", monospace';

/** Eleven stops from white to near-black, pulled towards the preset's neutral hue. */
const buildNeutrals = (hue: string, saturation: number, mode: 'light' | 'dark'): NeutralScale => {
  const tint = (base: string) => (saturation <= 0 ? base : mix(base, hue, saturation));

  if (mode === 'light') {
    return {
      n0: '#FFFFFF',
      n50: tint('#FAFAFA'),
      n100: tint('#F4F4F4'),
      n200: tint('#EAEAEA'),
      n300: tint('#DCDCDC'),
      n400: tint('#BDBDBD'),
      n500: tint('#8E8E8E'),
      n600: tint('#6B6B6B'),
      n700: tint('#4A4A4A'),
      n800: tint('#2C2C2C'),
      n900: tint('#1A1A1A'),
    };
  }

  // Dark is not the light scale reversed: pure white on near-black halates.
  return {
    n0: tint('#0F0F0F'),
    n50: tint('#161616'),
    n100: tint('#1C1C1C'),
    n200: tint('#242424'),
    n300: tint('#2E2E2E'),
    n400: tint('#3D3D3D'),
    n500: tint('#6E6E6E'),
    n600: tint('#9A9A9A'),
    n700: tint('#C4C4C4'),
    n800: tint('#E4E4E4'),
    n900: tint('#F5F5F5'),
  };
};

const buildElevation = (mode: 'light' | 'dark', strength: number): ElevationTokens => {
  if (strength <= 0) {
    return { e0: 'none', e1: 'none', e2: 'none', e3: 'none', e4: 'none' };
  }
  // Dark surfaces barely show a shadow, so they carry a heavier one.
  const k = mode === 'dark' ? strength * 1.9 : strength;
  const c = mode === 'dark' ? '#000000' : '#1A1A2E';
  return {
    e0: 'none',
    e1: `0 1px 2px ${alphaOf(c, 0.06 * k)}, 0 1px 3px ${alphaOf(c, 0.04 * k)}`,
    e2: `0 2px 6px ${alphaOf(c, 0.08 * k)}, 0 1px 2px ${alphaOf(c, 0.05 * k)}`,
    e3: `0 8px 20px ${alphaOf(c, 0.1 * k)}, 0 2px 6px ${alphaOf(c, 0.06 * k)}`,
    e4: `0 18px 44px ${alphaOf(c, 0.14 * k)}, 0 6px 14px ${alphaOf(c, 0.08 * k)}`,
  };
};

const DARK_LABEL = '#0A0A0A';

/**
 * Lightens a brand colour for a dark canvas. Mid-tones can land where neither
 * black nor white labels reach 4.5:1, so the lift grows until one does.
 */
const liftForDark = (hex: string): string => {
  let amount = 0.18;
  let lifted = lighten(hex, amount);
  while (contrastRatio(lifted, readableOn(lifted, DARK_LABEL)) < 4.5 && amount < 0.6) {
    amount += 0.04;
    lifted = lighten(hex, amount);
  }
  return lifted;
};

/** A ramp from `main`, with any pinned stops applied over it. */
const pinnedRamp = (
  main: string,
  contrastText: string | undefined,
  pin?: Partial<ColorRamp>,
): ColorRamp => ({
  ...rampFrom(pin?.main ?? main, pin?.contrastText ?? contrastText),
  ...pin,
});

const pinnedStatus = (base: StatusColors, pins?: ModeOverrides['status']): StatusColors => {
  if (!pins) return base;
  const out = { ...base };
  for (const key of Object.keys(pins) as (keyof StatusColors)[]) {
    const pin = pins[key];
    if (pin) out[key] = pinnedRamp(base[key].main, base[key].contrastText, pin);
  }
  return out;
};

/** Builds one mode's token set from a seed. */
export const deriveTokens = (seed: ThemeSeed, mode: 'light' | 'dark'): ThemeTokens => {
  const isDark = mode === 'dark';
  const pins = seed.modes?.[mode];
  const n = buildNeutrals(seed.neutralHue ?? '#64748B', seed.neutralSaturation ?? 0, mode);

  // On a dark canvas the authored brand colour is usually too heavy, so it is lifted.
  const secondarySeed = seed.secondary ?? darken(seed.primary, 0.3);
  const accentSeed = seed.accent ?? lighten(seed.primary, 0.25);

  // The authored contrast colour belongs to the authored primary. The lifted
  // dark-mode primary picks its own, or white labels fall below 4.5:1.
  const primary = pinnedRamp(
    isDark ? liftForDark(seed.primary) : seed.primary,
    isDark ? readableOn(liftForDark(seed.primary), DARK_LABEL) : seed.primaryContrast,
    pins?.primary,
  );
  const secondary = pinnedRamp(
    isDark ? lighten(secondarySeed, 0.18) : secondarySeed,
    undefined,
    pins?.secondary,
  );
  const accent = rampFrom(isDark ? lighten(accentSeed, 0.12) : accentSeed);
  const status = pinnedStatus(isDark ? STATUS_DARK : STATUS_LIGHT, pins?.status);

  const surface: SurfaceTokens = {
    canvas: isDark ? n.n0 : n.n50,
    paper: isDark ? n.n100 : '#FFFFFF',
    raised: isDark ? n.n200 : '#FFFFFF',
    sunken: isDark ? n.n50 : n.n100,
    scrim: alphaOf('#000000', isDark ? 0.62 : 0.42),
    ...pins?.surface,
  };

  const text: TextTokens = {
    primary: n.n900,
    secondary: n.n600,
    disabled: isDark ? n.n500 : n.n400,
    inverse: isDark ? n.n0 : '#FFFFFF',
    link: primary.main,
    ...pins?.text,
  };

  const border: BorderTokens = {
    subtle: n.n200,
    default: n.n300,
    strong: n.n400,
    focus: primary.main,
    ...pins?.border,
  };

  // Complete colours rather than alphas — see StateTokens.
  const state = {
    hover: isDark ? mix(surface.paper, '#FFFFFF', 0.05) : mix(surface.paper, primary.main, 0.05),
    selected: isDark
      ? mix(surface.paper, primary.main, 0.22)
      : mix(surface.paper, primary.main, 0.12),
    selectedHover: isDark
      ? mix(surface.paper, primary.main, 0.28)
      : mix(surface.paper, primary.main, 0.18),
    pressed: isDark
      ? mix(surface.paper, primary.main, 0.34)
      : mix(surface.paper, primary.main, 0.22),
    disabledBg: isDark ? mix(surface.paper, '#FFFFFF', 0.03) : n.n100,
  };

  // A dark sidebar is a near-black pulled towards the brand, not the brand itself.
  const sidebarBg =
    seed.darkSidebar && !isDark ? mix('#1B1B1F', seed.primary, 0.14) : surface.paper;
  const sidebarOnDark = Boolean(seed.darkSidebar) && !isDark;

  const navBg = seed.brandNav && !isDark ? primary.main : surface.paper;
  const navOnBrand = Boolean(seed.brandNav) && !isDark;

  const drawerHeaderBg =
    seed.brandDrawerHeader === false
      ? surface.raised
      : isDark
        ? mix(surface.paper, '#FFFFFF', 0.05)
        : primary.main;
  const drawerHeaderOnBrand = seed.brandDrawerHeader !== false && !isDark;

  const radius = { sm: 4, md: 6, lg: 8, pill: 999, ...seed.radius };

  return {
    brand: { primary, secondary, accent },
    neutral: n,
    status,
    surface,
    text,
    border,
    state,
    elevation: buildElevation(mode, seed.elevationStrength ?? 1),
    radius,
    typography: {
      fontFamily: seed.fontFamily ?? DEFAULT_FONT,
      monoFamily: seed.monoFamily ?? DEFAULT_MONO,
      baseSize: seed.baseSize ?? 14,
      lineHeight: 1.5,
      weightRegular: 400,
      weightMedium: 600,
      weightBold: 700,
      headingLetterSpacing: seed.headingLetterSpacing ?? -0.2,
    },
    component: {
      nav: {
        background: navBg,
        text: navOnBrand ? primary.contrastText : text.primary,
        activeText: navOnBrand ? primary.contrastText : primary.main,
        activeIndicator: navOnBrand ? primary.contrastText : primary.main,
        border: navOnBrand ? alphaOf('#FFFFFF', 0.18) : border.subtle,
      },
      sidebar: {
        background: sidebarBg,
        text: sidebarOnDark ? '#E8E8EC' : text.primary,
        mutedText: sidebarOnDark ? alphaOf('#FFFFFF', 0.55) : text.secondary,
        activeBackground: sidebarOnDark ? alphaOf('#FFFFFF', 0.12) : state.selected,
        activeText: sidebarOnDark ? '#FFFFFF' : primary.main,
        hoverBackground: sidebarOnDark ? alphaOf('#FFFFFF', 0.07) : state.hover,
        border: sidebarOnDark ? alphaOf('#FFFFFF', 0.1) : border.subtle,
      },
      drawer: {
        headerBackground: drawerHeaderBg,
        headerText: drawerHeaderOnBrand ? primary.contrastText : text.primary,
        background: surface.paper,
        footerBackground: surface.paper,
        border: border.subtle,
      },
      table: {
        headerBackground: mix(surface.paper, primary.main, isDark ? 0.12 : 0.04),
        headerText: text.primary,
        rowBackground: surface.paper,
        rowAltBackground: seed.stripedTables ? surface.sunken : surface.paper,
        rowHover: state.hover,
        rowSelected: state.selected,
        border: border.subtle,
      },
      input: {
        background: surface.paper,
        text: text.primary,
        placeholder: text.disabled,
        border: border.default,
        hoverBorder: border.strong,
        focusBorder: primary.main,
        disabledBackground: state.disabledBg,
        errorBorder: status.error.main,
      },
      button: {
        primaryBackground: primary.main,
        primaryText: primary.contrastText,
        primaryHover: isDark ? lighten(primary.main, 0.12) : darken(primary.main, 0.12),
        secondaryBorder: border.default,
        secondaryText: text.primary,
        secondaryHover: state.hover,
        ghostText: primary.main,
        ghostHover: state.hover,
        radius: Math.max(radius.md - 2, 0),
      },
    },
  };
};
